<template>
    <div
        class="cl-label"
        :class="[`cl-label--${color}`, `cl-label--text-${textColor}`, size === 'small' && 'cl-label--small']"
    >
        {{ label }}
    </div>
</template>

<script setup>
import { computed } from 'vue'

/**
 * @component ColoredLabels
 * @description Badge colorato con testo in maiuscolo. Usare per etichette di stato, tag, badge "NEW".
 *
 * @prop {String}  label      - Testo del badge
 * @prop {String}  color      - Variante colore: 'red' | 'green' | 'yellow' | 'navy' | 'acqua' | 'gray'
 * @prop {String}  textColor  - Colore testo: 'white' | 'black' (default: 'white')
 * @prop {Number}  saturation - Intensità colore 0–200, default 100. Agisce solo sul canale S dell'HSL,
 *                              senza alterare il colore del testo (a differenza di filter:saturate).
 */
const props = defineProps({
    label: { type: String, default: '' },
    color: {
        type: String,
        default: 'red',
        validator: v => ['red', 'green', 'yellow', 'navy', 'acqua', 'gray', 'transparent'].includes(v)
    },
    textColor: {
        type: String,
        default: 'white',
        validator: v => ['white', 'black'].includes(v)
    },
    saturation: {
        type: Number,
        default: 100,
        validator: v => v >= 0 && v <= 200
    },
    size: {
        type: String,
        default: 'medium',
        validator: v => ['small', 'medium'].includes(v)
    }
})

// Fattore moltiplicativo per il canale S in hsl() — 100 → 1.0, 50 → 0.5, 200 → 2.0
const satFactor = computed(() => props.saturation / 100)
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";
/*
  Colori definiti come HSL custom properties (--cl-h, --cl-s, --cl-l).
  La saturazione viene applicata moltiplicando --cl-s per satFactor nel calc(),
  così il testo non viene alterato (a differenza di filter: saturate()).
*/
.cl-label {
    display: inline-block;
    padding: 5px 12px;
    border-radius: 5px;
    font-size: $font14;
    font-family: OpenSansSemiBold, sans-serif;
    line-height: 1.4;
    white-space: nowrap;
    text-transform: uppercase;
    background-color: hsl(var(--cl-h), calc(var(--cl-s) * v-bind(satFactor)), var(--cl-l));
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.10);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

/* text color */
.cl-label--text-white { color: #fff; }
.cl-label--text-black { color: $blackTwo; }

/* color variants — fonte: $pal-* in variables.scss */
.cl-label--red    { --cl-h: 12;               --cl-s: 100%;             --cl-l: 88%; }
.cl-label--green  { --cl-h: #{$pal-green-h};  --cl-s: #{$pal-green-s};  --cl-l: 60%; }
.cl-label--yellow { --cl-h: #{$pal-yellow-h}; --cl-s: 100%;            --cl-l: 88%; }
.cl-label--navy   { --cl-h: #{$pal-navy-h};   --cl-s: #{$pal-navy-s};   --cl-l: #{$pal-navy-l}; }
.cl-label--acqua  { --cl-h: #{$pal-acqua-h};  --cl-s: #{$pal-acqua-s};  --cl-l: #{$pal-acqua-l}; }
.cl-label--gray   { --cl-h: #{$pal-gray-h};   --cl-s: #{$pal-gray-s};   --cl-l: #{$pal-gray-l}; }
.cl-label--transparent {
  background-color: transparent;
  box-shadow: none;
  border: 1px solid $graySeven;
  color: $graySeven;
}

.cl-label--small {
  padding: 2px 7px;
  font-size: $fontSmall;
}
</style>
